import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { SimilarCase } from "@/types/spectrum";
import { panelCls } from "@/utils/panelCls";

interface SimilarCaseDBProps {
  similarCases: SimilarCase[];
  /** MS 원본 결측(D19) — 동정 불가로 유사 사례 매칭 불가. 안내 표시. */
  isMsMissing?: boolean;
}

const SimilarCaseDB = ({
  similarCases,
  isMsMissing = false,
}: SimilarCaseDBProps) => {
  if (isMsMissing) {
    return (
      <div
        className={panelCls(
          "standard",
          "justify-center gap-4 flex flex-col m-auto h-full",
        )}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
          유사 사례 DB
        </h3>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            MS 미수집 — 유사 사례 매칭 불가
          </p>
          <p className="mt-2 text-xs text-gray-500">
            동정 결과가 없어 사례 비교를 수행할 수 없음 (결측이며 비검출 아님)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={panelCls(
        "standard",
        "justify-center gap-10 flex flex-col m-auto h-full",
      )}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
        유사 사례 DB
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={similarCases.map((case_) => ({
              name: case_.case_id,
              value: case_.similarity,
              incident_type: case_.incident_type,
              dominant_compounds: case_.dominant_compounds,
            }))}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, value }) =>
              `${name}\n${(value as number).toFixed(0)}%`
            }
            outerRadius={80}
            fill="#14b8a6"
            dataKey="value"
            style={{ fontSize: "10px" }}
          >
            {similarCases.map((entry, index) => (
              <Cell
                key={`cell-${entry.case_id}`}
                fill={["#14b8a6", "#0d9488", "#0f766e", "#134e4a"][index % 4]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [
              `${(value as number).toFixed(1)}%`,
              "유사도",
            ]}
            labelFormatter={(label) => `Case: ${label}`}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                    <div className="text-sm font-medium text-gray-900">
                      {data.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      유사도: {(data.value as number).toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {data.incident_type}
                    </div>
                    <div className="text-xs text-gray-500">
                      {data.dominant_compounds}
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="mt-3 space-y-2">
        {similarCases.slice(0, 2).map((case_) => (
          <div
            key={case_.case_id}
            className="p-2 bg-teal-50 border border-teal-200 rounded text-xs"
          >
            <div className="font-medium text-teal-800">{case_.case_id}</div>
            <div className="text-teal-600">
              유사도: {case_.similarity.toFixed(0)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SimilarCaseDB;
